import { useEffect, useState } from 'react';
import useSWR from 'swr';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, LogOut, LayoutDashboard, Inbox, Package, ShoppingBasket,
  Receipt, Building2, Users, Repeat, Menu, X, BarChart3,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { ADMIN_DASH, ADMIN_QUOTES } from '@/constants/testIds';
import { fetchAdminStats } from '@/lib/api';
import OverviewPanel from '@/components/admin/OverviewPanel';
import AnalyticsPanel from '@/components/admin/AnalyticsPanel';
import InquiriesPanel from '@/components/admin/InquiriesPanel';
import ProductsPanel from '@/components/admin/ProductsPanel';
import QuotesPanel from '@/components/admin/QuotesPanel';
import OrdersPanel from '@/components/admin/OrdersPanel';
import WholesaleApplicationsPanel from '@/components/admin/WholesaleApplicationsPanel';
import CustomersPanel from '@/components/admin/CustomersPanel';
import SubscriptionsPanel from '@/components/admin/SubscriptionsPanel';
import WarmImageCacheButton from '@/components/admin/WarmImageCacheButton';
import GlobalSearch from '@/components/admin/GlobalSearch';
import Seo from '@/components/Seo';

// Sidebar navigation model — grouped for a modern SaaS admin feel.
// `badge` reads a key off the /admin/overview/stats response and shows a
// notification pill when > 0 so admins can see "needs action" at a glance.
const NAV_GROUPS = [
  {
    label: 'Overview',
    items: [
      { slug: 'overview',   label: 'Dashboard',  icon: LayoutDashboard, testId: 'admin-nav-overview' },
      { slug: 'analytics',  label: 'Analytics',  icon: BarChart3,       testId: 'admin-nav-analytics' },
    ],
  },
  {
    label: 'Trade',
    items: [
      { slug: 'inquiries',  label: 'Inquiries',   icon: Inbox,          testId: ADMIN_DASH.navInquiries, badge: 'inquiries_new' },
      { slug: 'wholesale',  label: 'Applications', icon: Building2,     testId: 'admin-nav-wholesale',   badge: 'wholesale_pending' },
      { slug: 'quotes',     label: 'Quotes',      icon: ShoppingBasket, testId: ADMIN_QUOTES.navTab,     badge: 'quotes_new' },
    ],
  },
  {
    label: 'Commerce',
    items: [
      { slug: 'orders',        label: 'Orders',        icon: Receipt,  testId: 'admin-nav-orders' },
      { slug: 'subscriptions', label: 'Subscriptions', icon: Repeat,   testId: 'admin-nav-subscriptions' },
      { slug: 'customers',     label: 'Customers',     icon: Users,    testId: 'admin-nav-customers' },
      { slug: 'products',      label: 'Products',      icon: Package,  testId: ADMIN_DASH.navProducts },
    ],
  },
];

const ALL_ITEMS = NAV_GROUPS.flatMap((g) => g.items);

function BadgePill({ count }) {
  if (!count) return null;
  return (
    <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-[18px] px-1.5 text-[10px] font-medium bg-[var(--mw-primary)] text-white rounded-full tabular-nums">
      {count > 99 ? '99+' : count}
    </span>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState(() => {
    if (typeof window === 'undefined') return 'overview';
    const hash = window.location.hash.replace('#', '');
    return ALL_ITEMS.find((i) => i.slug === hash) ? hash : 'overview';
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  // Sync tab to URL hash so admins can bookmark/reload
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.history.replaceState(null, '', `#${tab}`);
  }, [tab]);

  // Pull stats for sidebar badges — refresh every 60s
  const { data: stats } = useSWR('admin-stats', fetchAdminStats, { refreshInterval: 60000 });

  const goTo = (slug) => {
    setTab(slug);
    setMobileOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const activeItem = ALL_ITEMS.find((i) => i.slug === tab) || ALL_ITEMS[0];

  return (
    <div
      data-testid={ADMIN_DASH.page}
      className="min-h-screen bg-[var(--mw-bg)] text-white flex"
    >
      <Seo title="Admin Console" path="/admin" noindex />

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          className="lg:hidden fixed inset-0 bg-black/60 z-40 backdrop-blur-sm"
        />
      )}

      {/* Sidebar */}
      <aside
        data-testid="admin-sidebar"
        className={`fixed lg:sticky top-0 left-0 z-50 h-screen w-64 bg-[#080808] border-r border-white/10 flex flex-col transition-transform duration-300 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        {/* Brand */}
        <div className="h-20 px-6 flex items-center justify-between border-b border-white/10 shrink-0">
          <div className="flex items-center gap-3">
            <span className="block w-2 h-2 rounded-full bg-[var(--mw-primary)]" />
            <span className="font-display text-lg tracking-wide">Trade Console</span>
          </div>
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="lg:hidden text-white/60 hover:text-white"
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
        </div>

        {/* Nav groups */}
        <nav className="flex-1 overflow-y-auto py-6 px-3 space-y-6">
          {NAV_GROUPS.map((group) => (
            <div key={group.label}>
              <div className="px-3 mb-2 text-[9px] uppercase tracking-[0.32em] text-white/35">
                {group.label}
              </div>
              <div className="space-y-0.5">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const active = tab === item.slug;
                  const badgeCount = item.badge ? stats?.[item.badge] : 0;
                  return (
                    <button
                      key={item.slug}
                      type="button"
                      onClick={() => goTo(item.slug)}
                      data-testid={item.testId}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 text-[13px] transition-colors ${
                        active
                          ? 'bg-[var(--mw-primary)]/12 text-white border-l-2 border-[var(--mw-primary)]'
                          : 'text-white/60 hover:text-white hover:bg-white/[0.04] border-l-2 border-transparent'
                      }`}
                    >
                      <Icon size={15} className={active ? 'text-[var(--mw-primary)]' : ''} />
                      <span className="flex-1 text-left">{item.label}</span>
                      <BadgePill count={badgeCount} />
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Sidebar footer — back to site + sign out */}
        <div className="p-3 border-t border-white/10 space-y-1 shrink-0">
          <Link
            to="/"
            className="flex items-center gap-3 px-3 py-2.5 text-[13px] text-white/50 hover:text-white hover:bg-white/[0.04] transition-colors"
          >
            <ArrowLeft size={14} />
            Back to site
          </Link>
          <button
            type="button"
            onClick={logout}
            data-testid={ADMIN_DASH.logout}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-[13px] text-white/60 hover:text-white hover:bg-white/[0.04] transition-colors"
          >
            <LogOut size={14} />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar */}
        <header className="sticky top-0 z-30 h-16 bg-[var(--mw-bg)]/95 backdrop-blur border-b border-white/10 flex items-center gap-4 px-4 sm:px-6 lg:px-10 shrink-0">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="lg:hidden text-white/70 hover:text-white"
            aria-label="Open menu"
          >
            <Menu size={20} />
          </button>
          <div className="hidden sm:block min-w-0">
            <div className="text-[9px] uppercase tracking-[0.32em] text-white/40">
              {activeItem.label}
            </div>
          </div>
          <div className="flex-1 flex justify-center max-w-md ml-auto lg:ml-0">
            <GlobalSearch onNavigate={goTo} />
          </div>
          <div className="hidden md:block">
            <WarmImageCacheButton />
          </div>
          {user && user !== false && (
            <div
              data-testid={ADMIN_DASH.userBadge}
              className="hidden md:flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-white/50 shrink-0"
            >
              <span className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center text-white/80 text-xs">
                {(user.name || user.email || '?').charAt(0).toUpperCase()}
              </span>
              <span className="hidden lg:inline">{user.name || user.email}</span>
            </div>
          )}
        </header>

        {/* Panel content */}
        <main className="flex-1 px-4 sm:px-6 lg:px-10 py-8 lg:py-10 overflow-x-hidden">
          {tab === 'overview'      && <OverviewPanel onNavigate={goTo} />}
          {tab === 'analytics'     && <AnalyticsPanel />}
          {tab === 'inquiries'     && <InquiriesPanel />}
          {tab === 'wholesale'     && <WholesaleApplicationsPanel />}
          {tab === 'quotes'        && <QuotesPanel />}
          {tab === 'orders'        && <OrdersPanel />}
          {tab === 'subscriptions' && <SubscriptionsPanel />}
          {tab === 'customers'     && <CustomersPanel />}
          {tab === 'products'      && <ProductsPanel />}
        </main>
      </div>
    </div>
  );
}
